import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../axiosInstance/axiosInstance";
import { ApiErrorResponse } from "../../../api/types/ApiErrorResponse";
import { toApiError } from "../../../api/utils/errorHandling";
import { EnrolmentResponse } from "../contracts/EnrolmentResponse";
import { SignEnrolmentRequest } from "../contracts/SignEnrolmentRequest";
import { mapResponseToEnrolment } from "../mappers/TrainingResponseMapper";
import { Enrolment } from "../models/Enrolment";

/** The member's signature on an enrolment's training record, drawn on the school's screen. */
const BASE = "/training";
type Rejection = { rejectValue: ApiErrorResponse };

const signatureUrl = (memberId: string, enrolmentId: string): string =>
  `${BASE}/members/${memberId}/enrolments/${enrolmentId}/signature`;

/** Stores the drawn signature; signing again replaces the earlier one. */
export const signEnrolment = createAsyncThunk<
  Enrolment,
  { memberId: string; enrolmentId: string; request: SignEnrolmentRequest },
  Rejection
>("training/signEnrolment", async ({ memberId, enrolmentId, request }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.put<EnrolmentResponse>(signatureUrl(memberId, enrolmentId), request);
    return mapResponseToEnrolment(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to save the signature"));
  }
});

/** Owner/Admin only: removes the signature so a new one can be collected. */
export const removeEnrolmentSignature = createAsyncThunk<
  Enrolment,
  { memberId: string; enrolmentId: string },
  Rejection
>("training/removeEnrolmentSignature", async ({ memberId, enrolmentId }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.delete<EnrolmentResponse>(signatureUrl(memberId, enrolmentId));
    return mapResponseToEnrolment(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to remove the signature"));
  }
});
